import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { UpsellMessageType } from '../components/targeted-upsell/TargetedUpsell';
import { OpenMarketOptions } from '../components/upsell-feedback/UpsellFeedback';
import {
  LatestPeriodSequenceV2,
  getLatestSequencePeriodForSubscription,
  getUpsellMessage,
} from '../services/Market.service';

export const useUpsellMessage = (
  cells: string[],
  openMarketsOptions: OpenMarketOptions[]
): {
  showUpsellMessage: boolean;
  upsellMessageForSlide: UpsellMessageType;
} => {
  const [isMounted, setIsMounted] = useState<boolean>(true);
  const [selectedCells, setSelectedCells] = useState<string[]>([]);
  const [selectedOpenMarketsOptions, setSelectedOpenMarketsOptions] = useState<OpenMarketOptions[]>([]);
  const [latestSequenceData4x6x, setLatestSequenceData4x6x] = useState<LatestPeriodSequenceV2[]>(undefined);
  const [showUpsellMessage, setShowUpsellMessage] = useState<boolean>(false);
  const [upsellMessageForSlide, setUpsellMessageForSlide] = useState<UpsellMessageType | undefined>();

  const { isAuthenticated, getAccessToken } = useAuthentication();

  useEffect(() => {
    setSelectedCells(cells);
    setSelectedOpenMarketsOptions(openMarketsOptions);
    setShowUpsellMessage(false);
    setUpsellMessageForSlide(undefined);
  }, [cells, openMarketsOptions]);

  useEffect(() => {
    (async () => {
      const token = await getAccessToken();
      const result = await getLatestSequencePeriodForSubscription(token);
      if (isMounted) {
        setLatestSequenceData4x6x(result);
      }
    })();

    return () => {
      setIsMounted(false);
    };
    // disabling exhaustive deps to prevent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUpsellMessage = useCallback(() => {
    if (
      Array.isArray(selectedOpenMarketsOptions) &&
      Array.isArray(selectedCells) &&
      selectedCells.length === 1 &&
      Array.isArray(latestSequenceData4x6x) &&
      latestSequenceData4x6x.length
    ) {
      const latestSequenceValue = latestSequenceData4x6x.find(sequence => sequence?.cell === selectedCells[0]);
      const selectedMarket = selectedOpenMarketsOptions.find(market => market?.value === latestSequenceValue?.cell);
      const selectedMarketPeriods = selectedMarket?.periods;
      const selectedMarketPeriod = selectedMarketPeriods?.find(
        period => period.periodicity === latestSequenceValue.periodicity && period.periodicity === 'Monthly'
      );

      const selectedHasOutdatedData =
        Number(latestSequenceValue?.latestPublishedPeriodSequence) > Number(selectedMarketPeriod?.sequence);

      if (selectedHasOutdatedData && selectedCells.length === 1) {
        return {
          countryCode: selectedMarket?.value.split('-')[0],
          itemGroupCode: selectedMarket?.value.split('-')[1],
          dataPeriod: latestSequenceValue?.latestPublishedPeriodSequence,
          dataPeriodDisplay: latestSequenceValue?.latestPublishedPeriodSequenceDisplay,
        };
      }
    }
  }, [latestSequenceData4x6x, selectedCells, selectedOpenMarketsOptions]);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated) {
        return;
      }
      const token = await getAccessToken();
      const upsellDataForModal = await checkUpsellMessage();
      if (upsellDataForModal) {
        const upsellMessageRequestBody = {
          countryCode: upsellDataForModal.countryCode,
          itemGroupCode: upsellDataForModal.itemGroupCode,
          dataPeriod: upsellDataForModal.dataPeriod,
        };

        const result = await getUpsellMessage(token, upsellMessageRequestBody);

        if (result && result.upsellMessage) {
          setUpsellMessageForSlide({ ...result, dataPeriodDisplay: upsellDataForModal.dataPeriodDisplay });
        }
      }
    })();

    return () => {
      setIsMounted(false);
    };
  }, [
    getAccessToken,
    isMounted,
    latestSequenceData4x6x,
    checkUpsellMessage,
    isAuthenticated,
    openMarketsOptions,
    selectedCells,
  ]);

  return useMemo(
    () => ({
      showUpsellMessage,
      upsellMessageForSlide,
    }),
    [showUpsellMessage, upsellMessageForSlide]
  );
};
